/**
 * Frontend mirror of `backend/utils/tax_calculator.py`.
 *
 * Single source of truth for sales-tax rates so the Review Order cost
 * summary and the backend Quote / Order Confirmation PDFs always agree
 * to the dollar — no surprise on the customer's invoice.
 *
 * If the rate table changes server-side, update this file too. The
 * unit tests in `backend/tests/test_tax_calculator.py` are the canon.
 */

// Canada — provincial tax rates from the customer's iter108 reference
// chart. Combined GST + HST/QST/PST per province.
const CA_RATES = {
  BC: { rate: 0.05, label: 'GST 5%' },
  AB: { rate: 0.05, label: 'GST 5%' },
  SK: { rate: 0.05, label: 'GST 5%' },
  MB: { rate: 0.05, label: 'GST 5%' },
  ON: { rate: 0.13, label: 'HST 13%' },
  QC: { rate: 0.05, label: 'GST 5%' },
  NB: { rate: 0.15, label: 'HST 15%' },
  NL: { rate: 0.15, label: 'HST 15%' },
  NS: { rate: 0.14, label: 'HST 14%' },
  NT: { rate: 0.05, label: 'GST 5%' },
  NU: { rate: 0.05, label: 'GST 5%' },
  YT: { rate: 0.05, label: 'GST 5%' },
  PE: { rate: 0.15, label: 'HST 15%' },
  PEI: { rate: 0.15, label: 'HST 15%' },
};

const CA_ALIASES = {
  'BRITISH COLUMBIA': 'BC',
  ALBERTA: 'AB',
  SASKATCHEWAN: 'SK',
  MANITOBA: 'MB',
  ONTARIO: 'ON',
  QUEBEC: 'QC',
  QUÉBEC: 'QC',
  'NEW BRUNSWICK': 'NB',
  NEWFOUNDLAND: 'NL',
  'NEWFOUNDLAND AND LABRADOR': 'NL',
  'NOVA SCOTIA': 'NS',
  'NORTHWEST TERRITORIES': 'NT',
  NUNAVUT: 'NU',
  YUKON: 'YT',
  'YUKON TERRITORY': 'YT',
  'PRINCE EDWARD ISLAND': 'PE',
};

function normalizeCountry(country) {
  if (!country) return '';
  const c = String(country).trim().toUpperCase();
  if (['CA', 'CAN', 'CANADA'].includes(c)) return 'CA';
  if (['US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'].includes(c)) return 'US';
  return c;
}

function normalizeProvince(state) {
  if (!state) return '';
  const s = String(state).trim().toUpperCase();
  if (CA_RATES[s]) return s;
  return CA_ALIASES[s] || s;
}

/**
 * Look up the (rate, label) pair for a ship-to country/state.
 * Returns { rate: 0, label: 'Tax' } for unknown combos.
 */
export function lookupRate(country, state) {
  const c = normalizeCountry(country);
  if (c === 'CA') {
    const prov = normalizeProvince(state);
    if (CA_RATES[prov]) return CA_RATES[prov];
    return { rate: 0, label: 'Tax' };
  }
  if (c === 'US') return { rate: 0, label: 'Tax exempt (US)' };
  return { rate: 0, label: 'Tax' };
}

/**
 * Compute the dollar tax on `(itemsSubtotal + shipping)` for the given
 * ship-to address. Mirrors `tax_breakdown` on the backend.
 */
export function taxBreakdown(itemsSubtotal, shipping, country, state) {
  const { rate, label } = lookupRate(country, state);
  const items = Number(itemsSubtotal) || 0;
  const ship = Number(shipping) || 0;
  const taxable = Math.max(items + ship, 0);
  return {
    rate,
    label,
    taxableSubtotal: Math.round(taxable * 100) / 100,
    taxAmount: Math.round(taxable * rate * 100) / 100,
    country: normalizeCountry(country),
    state: normalizeProvince(state),
  };
}
